<template>
  <va-form
    :id="isEdit ? calendar.id : ''"
    ref="form"
    :item="calendar"
    v-model="form"
    :required-fields-set="requiredFieldsSet"
    :modify-before-send="modifyBeforeSend"
    :success-redirect-link="successRedirectLink"
    @finishedSaving="finishedSaving">
    <v-card>
      <v-card-title>{{ $t("resources.calendars.details") }}</v-card-title>
      <va-text-input
        required
        v-model="form.name"
        source="name"
        class="spacing-top--16"
        :label="$t('resources.calendars.fields.name')"
        :placeholder="$t('resources.calendars.placeholders.name')"></va-text-input>

      <div class="page-wrapper spacing-top--16" v-if="form.pages && form.pages.length > 0">
        <div class="page-label">
          {{ $t("resources.calendars.fields.page") }}
        </div>
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page.id">
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>
      <div v-if="form.updatedAt" class="spacing-top--16">
        <div class="updated-at-label">
          {{ $t("resources.calendars.fields.last_edited") }}
        </div>
        <div class="updated-at-time">
          {{ getFullDateAndTime(form.updatedAt) }}
          {{ form.updatedBy && `by ${form.updatedBy.firstName} ${form.updatedBy.lastName}` }}
        </div>
      </div>
      <div v-if="form.createdAt" class="spacing-top--16">
        <div class="updated-at-label">
          {{ $t("resources.calendars.fields.created") }}
        </div>
        <div class="updated-at-time">
          {{ getFullDateAndTime(form.createdAt) }}
          {{ form.createdBy && `by ${form.createdBy.firstName} ${form.createdBy.lastName}` }}
        </div>
      </div>
    </v-card>
    <v-card class="spacing-top--16">
      <v-card-title>{{ $t("resources.calendars.weekday_display_title") }}</v-card-title>
      <v-card-subtitle>{{ $t("resources.calendars.weekday_display_subtitle") }}</v-card-subtitle>
      <v-data-table
        checkbox-color="primary"
        :headers="fields"
        :items="form.weekdayDisplay"
        v-model="selected"
        item-key="weekday"
        show-select
        hide-default-footer
        class="weekday-table"
        @input="items => setSelectedItems(items)">
        <template v-slot:[`header.data-table-select`]="{ props, on }">
          <v-checkbox
            class="d-inline-flex"
            :color="props.color"
            :indeterminate="props.indeterminate"
            :value="props.value"
            @change="on.input"
            hide-details></v-checkbox>
        </template>
        <template #item.weekday="{ item }">
          <div class="weekday-item">{{ item.weekday }}</div>
        </template>
        <template #item.duration="{ item, index }">
          <div v-if="item.ranges.length > 0" class="range-container">
            <div v-for="range in getRangeArray(item, index)" :key="range.key" class="range-inner-container">
              <v-text-field
                v-model="range.from"
                :placeholder="$t('resources.calendars.placeholders.time')"
                hide-details="auto"
                class="time-input-field"
                type="time"
                :rules="[rules.time]"
                validate-on-blur></v-text-field>
              <v-icon size="16">mdi-minus</v-icon>
              <v-text-field
                v-model="range.to"
                hide-details="auto"
                type="time"
                :placeholder="$t('resources.calendars.placeholders.time')"
                class="time-input-field"
                :rules="[rules.time]"
                validate-on-blur></v-text-field>
              <v-icon size="16" @click="deleteRange(item, range)">mdi-close</v-icon>
            </div>
            <div v-if="item.ranges.length > 3">
              <div v-if="!rangesShowAll[index]" class="range-action-button" @click="toggleRangeShowAll(index)">
                {{ $t("resources.calendars.show_all") }}
              </div>
              <div v-else class="range-action-button" @click="toggleRangeShowAll(index)">
                {{ $t("resources.calendars.collapse") }}
              </div>
            </div>
          </div>
          <div v-else class="no-duration">
            {{ $t("resources.calendars.no_duration") }}
          </div>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            small
            color="primary"
            outlined
            class="table-add-button"
            :class="{ 'table-add-button-disabled': addRangeDisabled(item) }"
            :disabled="addRangeDisabled(item)"
            @click="addNewDuration(item)">
            <v-icon
              size="16"
              color="primary"
              class="table-add-days-icon"
              :class="{ 'table-add-days-icon-disabled': addRangeDisabled(item) }"
              >mdi-plus</v-icon
            >
            {{ $t("resources.calendars.add") }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <v-card class="spacing-top--16">
      <div class="date-card-wrapper">
        <div>
          <v-card-title>{{ $t("resources.calendars.date_display_title") }}</v-card-title>
          <v-card-subtitle class="date-card-subtitle">{{
            $t("resources.calendars.date_display_subtitle")
          }}</v-card-subtitle>
        </div>
        <div>
          <v-menu v-model="menu" offset-y top left nudge-bottom="-10" :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <v-btn color="primary" outlined class="add-days-button" v-on="on">
                <v-icon size="16" class="add-days-icon">mdi-plus</v-icon>
                {{ $t("resources.calendars.select_date") }}
              </v-btn>
            </template>
            <v-date-picker class="date-picker" range v-model="selectedDate">
              <v-btn text color="secondary-btn" @click="menu = false">{{ $t("resources.calendars.cancel") }}</v-btn>
              <v-btn text color="secondary-btn" @click="addNewDate(selectedDate)">{{
                $t("resources.calendars.ok")
              }}</v-btn>
            </v-date-picker>
          </v-menu>
        </div>
      </div>
      <div v-if="form.dateDisplay.length > 0" class="spacing-top--16">
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removeDate(date)"
            v-for="(date, index) in getChipArray"
            :key="index + date.from + date.to">
            {{ getDateDisplay(date) }}
          </v-chip>
          <div v-if="form.dateDisplay.length > 12">
            <v-btn
              v-if="!showAll"
              text
              color="primary"
              class="chip-action-button"
              @click="showAll = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn v-else text color="primary" class="chip-action-button" @click="showAll = false" :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import { getFullDateAndTime } from "@core/utils/filter";
  import crudFormMixin from "@/mixins/crudFormMixin";
  import { format } from "date-fns";
  import transform from "lodash.transform";
  import RequiredFieldText from "@/components/custom/RequiredFieldText.vue";
  import { truncateText } from "@/utility/truncateText";

  export default {
    name: "CalendarForm",
    components: { RequiredFieldText },
    mixins: [crudFormMixin],
    props: {
      calendar: {
        type: Object,
        default: () => {},
      },
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        form: {},
        menu: false,
        showAll: false,
        showAllPageChips: false,
        rangesShowAll: {},
        selected: [],
        successRedirectLink: {},
        selectedDate: null,
        getFullDateAndTime,
        requiredFormFields: ["name"],
        fields: [
          {
            text: this.$t("resources.calendars.weekday"),
            align: "start",
            sortable: false,
            value: "weekday",
          },
          {
            text: this.$t("resources.calendars.duration_period"),
            sortable: false,
            align: "start",
            value: "duration",
          },
          {
            text: this.$t("resources.calendars.actions"),
            sortable: false,
            align: "end",
            value: "actions",
          },
        ],
        rules: {
          time: value => this.validateTime(value) || this.$t("resources.calendars.invalid_time"),
        },
      };
    },
    computed: {
      getChipArray() {
        if (this.showAll) {
          return this.form.dateDisplay;
        } else {
          return this.form.dateDisplay.slice(0, 12);
        }
      },
      getPageArray() {
        if (this.showAllPageChips) {
          return this.form.pages;
        } else {
          return this.form.pages.slice(0, 12);
        }
      },
    },
    beforeMount() {
      this.form = this.calendar ? this.calendar : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
        delete this.form.pages;
      }

      this.form.dateDisplay = this.calendar?.dateDisplay || [];
      this.form.weekdayDisplay = this.calendar?.weekdayDisplay || this.getEmptyWeekdayDisplay();

      if (this.calendar && !this.isEdit) {
        this.form.name = this.calendar?.name + " copy";
      }

      this.sortDates();
      this.transformWeekdayDisplay();

      this.form.weekdayDisplay.forEach(item => {
        item.ranges.forEach(range => {
          range.key = this.getRandomKey();
        });
      });

      this.selected = this.form.weekdayDisplay.filter(item => item.isSelected);
    },
    methods: {
      addNewDate(date) {
        this.menu = false;
        if (!date) return;

        if (date.length === 1) {
          this.form.dateDisplay.unshift({
            from: date[0],
            to: date[0],
          });
        } else {
          const startDate = new Date(date[0]) < new Date(date[1]) ? date[0] : date[1];
          const endDate = new Date(date[0]) < new Date(date[1]) ? date[1] : date[0];
          this.form.dateDisplay.unshift({
            from: startDate,
            to: endDate,
          });
        }
        this.selectedDate = null;
      },
      getDateDisplay(date) {
        if (date.from === date.to) {
          return format(new Date(date.from), "MMM dd, yyyy");
        }

        return `${format(new Date(date.from), "MMM dd, yyyy")} - ${format(new Date(date.to), "MMM dd, yyyy")}`;
      },
      removeDate(date) {
        this.form.dateDisplay = this.form.dateDisplay.filter(item => item !== date);
      },
      getEmptyWeekdayDisplay() {
        return {
          monday: {},
          tuesday: {},
          wednesday: {},
          thursday: {},
          friday: {},
          saturday: {},
          sunday: {},
        };
      },
      transformWeekdayDisplay() {
        this.form.weekdayDisplay = transform(
          this.form.weekdayDisplay,
          (result, value, key) => {
            result.push({ weekday: key, isSelected: value.isSelected, ranges: value.ranges ?? [] });
          },
          [],
        );
      },
      addNewDuration(item) {
        item.isSelected = true;
        item.ranges.unshift({
          from: "",
          to: "",
          key: this.getRandomKey(),
        });
        this.selected = [...this.selected, item];
      },
      validateTime(time) {
        return !!time.match(/^(?!00:00)(2[0-3]|[0-1][\d]):[0-5][\d]$/);
      },
      deleteRange(item, range) {
        item.ranges = item.ranges.filter(r => r !== range);
        if (item.ranges.length === 0) {
          this.selected = this.selected.filter(i => i !== item);
          item.isSelected = false;
        }
      },
      addRangeDisabled(item) {
        return item?.ranges?.length >= 24;
      },
      getRangeArray(item, index) {
        if (this.rangesShowAll?.[index]) {
          return item.ranges;
        } else {
          return item.ranges.slice(0, 3);
        }
      },
      toggleRangeShowAll(index) {
        if (Object.prototype.hasOwnProperty.call(this.rangesShowAll, index)) {
          this.$set(this.rangesShowAll, index, !this.rangesShowAll[index]);
        } else {
          this.$set(this.rangesShowAll, index, true);
        }
      },
      setSelectedItems(items) {
        this.form.weekdayDisplay.forEach(item => {
          item.isSelected = items.some(i => {
            if (i.weekday === item.weekday) {
              if (item.ranges.length === 0) {
                item.ranges.push({
                  from: "",
                  to: "",
                });
              }

              return true;
            }

            return false;
          });
        });
      },
      modifyBeforeSend(data) {
        data.pages = data.pages?.map(page => page["@id"]);

        data.weekdayDisplay = data.weekdayDisplay.reduce((acc, item) => {
          acc[item.weekday] = {
            isSelected: item.isSelected ?? false,
            ranges: item.ranges.map(range => ({
              from: range.from,
              to: range.to,
            })),
          };

          return acc;
        }, {});

        return data;
      },
      submit(toastText) {
        let displayError = false;
        this.successRedirectLink = {
          toastText,
        };

        this.setSubmitAttempted();

        Object.values(this.form.weekdayDisplay).forEach((item, parentIndex) => {
          item?.ranges.forEach((range, index) => {
            if (!this.validateTime(range.from) || !this.validateTime(range.to)) {
              displayError = true;
              if (index > 2) {
                this.rangesShowAll = { ...this.rangesShowAll, [parentIndex]: true };
              }
            }
          });
        });

        //Wait for input fields to appear and then try to validate
        setTimeout(() => {
          if (this.$refs.form.$refs.form.validate()) {
            this.$refs.form.onSubmit();
          } else {
            displayError = true;
            this.finishedSaving();
          }
        }, 0);

        if (displayError || !this.requiredFieldsSet) {
          this.$admin.toast.error(this.$t("resources.calendars.required_fields_missing"));
        }
      },
      getPageName(page) {
        return truncateText(page?.headline);
      },
      removePage(page) {
        this.form.pages = this.form.pages.filter(item => item["@id"] !== page["@id"]);
      },
      sortDates() {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        this.form.dateDisplay.forEach(item => {
          item.diffFromNow = new Date(item.from).getTime() - today;
        });

        this.form.dateDisplay.sort((a, b) => {
          if (a.fromTimestamp === today && b.fromTimestamp !== today) {
            return -1;
          }
          if (a.fromTimestamp !== today && b.fromTimestamp === today) {
            return 1;
          }
          if (a.diffFromNow >= 0 && b.diffFromNow >= 0) {
            return a.diffFromNow - b.diffFromNow;
          }
          if (a.diffFromNow < 0 && b.diffFromNow < 0) {
            return b.diffFromNow - a.diffFromNow;
          }

          return a.diffFromNow >= 0 ? -1 : 1;
        });

        this.form.dateDisplay.forEach(item => {
          delete item.diffFromNow;
        });
      },
      getRandomKey() {
        return Math.random().toString(36).substring(7);
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  ::v-deep {
    .date-card-subtitle {
      margin-bottom: 0;
    }
  }

  .date-card-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .add-days-icon {
    margin-right: 6px;
  }

  .add-days-button {
    padding: 12px 24px 12px 16px !important;
  }

  .date-picker {
    padding: 0;

    ::v-deep {
      .v-date-picker-table {
        height: unset;

        .v-btn--outlined {
          color: get-color("secondary-btn") !important;
        }
      }

      .v-picker__actions {
        padding: 5px 10px;
        justify-content: flex-end;

        .v-btn {
          padding: 12px;
          min-width: unset;
        }
      }
    }
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }

  .weekday-item {
    margin: 16px 0;
    @include text-body-2-bold();
    text-transform: uppercase;
  }

  .weekday-table {
    .table-add-button {
      margin: 14px 0;
      @include text-button-small();
      padding: 2px 12px !important;
      min-width: unset;

      &-disabled {
        border-color: get-color("disabled") !important;

        ::v-deep {
          span {
            color: get-color("disabled") !important;
          }
        }
      }
    }

    ::v-deep {
      table {
        min-width: unset;
      }

      td {
        vertical-align: top;

        .v-data-table__checkbox {
          margin: 16px 0;
        }
      }
    }
    .time-input-field {
      max-width: 108px;
      padding: 0;

      ::v-deep {
        input {
          padding: 7px 12px;
        }
      }
    }

    .table-add-days-icon.v-icon {
      transition: none;
      color: get-color("secondary-btn") !important;
      margin-right: 4px;
    }

    .table-add-days-icon-disabled.v-icon {
      color: get-color("disabled") !important;
    }

    .no-duration {
      margin: 16px 0;
      @include text-body-2();
      color: get-color("subtitle");
    }

    .range-container {
      margin: 10px 0;
      display: flex;
      flex-direction: column;
      row-gap: 8px;
    }

    .range-inner-container {
      display: flex;
      column-gap: 2px;
      align-items: baseline;
    }

    .range-action-button {
      cursor: pointer;
      color: get-color("primary");
    }
  }

  .page-chip-group {
    ::v-deep {
      .v-slide-group__content {
        padding: 0;
      }
    }
  }

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 8px;
  }

  .updated-at-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .updated-at-time {
    @include text-body-2();
    color: get-color("subtitle");
  }
</style>
